import { useCallback, useEffect, useRef, useState } from "react";
import {
  Search,
  FileText,
  MapPin,
  Trash2,
  Volume2,
  X,
  ArrowDownToLine,
  Download,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { beep, fmtDT } from "@/lib/sound";
import { useImportJob } from "@/hooks/useImportJob";
import { ImportProgress } from "@/components/ImportProgress";

const inputCls = "w-full bg-white border border-[#D9E3DC] rounded-md px-3 py-2 text-sm text-[#0F2A1F] focus:outline-none focus:ring-2 focus:ring-[#0F6B3A] placeholder:text-[#8A9A90]";

function QtdEditor({ initial, onConfirm, onCancel, testid }) {
  const ref = useRef(null);
  const [v, setV] = useState(initial || "");
  useEffect(() => { ref.current?.focus(); ref.current?.select(); }, []);
  const confirm = () => {
    const n = parseInt(v, 10);
    if (!n || n <= 0) { toast.error("Informe uma quantidade maior que zero"); ref.current?.focus(); return; }
    onConfirm(n);
  };
  return (
    <div className="flex items-center gap-2" data-testid={`${testid}-editor`}>
      <input ref={ref} type="number" min={1} inputMode="numeric" value={v} onChange={(e) => setV(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") confirm(); if (e.key === "Escape") onCancel(); }}
        data-testid={`${testid}-input`} placeholder="Qtd"
        className="w-24 h-12 text-2xl font-mono font-bold text-center border-2 border-[#F2B01E] rounded-md text-[#0F2A1F] focus:outline-none focus:ring-2 focus:ring-[#F2B01E]" />
      <button data-testid={`${testid}-confirm`} onClick={confirm} className="h-12 px-4 bg-[#F2B01E] hover:bg-[#B8860B] text-white font-bold rounded-md text-sm">Confirmar</button>
      <button onClick={onCancel} className="h-12 w-10 flex items-center justify-center text-[#5A6B62] hover:bg-[#F1F5F2] rounded-md"><X className="w-4 h-4" /></button>
    </div>
  );
}

function ProdutoRow({ p, grupo, lista, onConferir }) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const st = p.conferencia?.status;
  const [recent, setRecent] = useState(false);
  useEffect(() => {
    if (!p.conferencia?.at) { setRecent(false); return; }
    const age = Date.now() - new Date(p.conferencia.at).getTime();
    if (age > 5000) { setRecent(false); return; }
    setRecent(true);
    const t = setTimeout(() => setRecent(false), 5000 - age);
    return () => clearTimeout(t);
  }, [p.conferencia?.at]);
  const active = (s) => recent && st === s;
  const tid = `prod-${p.codigo}-${grupo.endereco.replace(/\s+/g, "")}`;
  const act = async (status, quantidade) => {
    setBusy(true);
    try { await onConferir({ lista, codigo: p.codigo, endereco: grupo.endereco, status, quantidade }); setEditing(false); }
    finally { setBusy(false); }
  };
  const btn = (s, label, cls, active) =>
    <button key={s} data-testid={`${tid}-${s.toLowerCase()}`} disabled={busy} onClick={() => (s === "QTD" ? setEditing(true) : act(s))}
      className={`h-11 min-w-[72px] px-3 text-xs font-bold tracking-wider rounded-md border-2 transition-colors disabled:opacity-60 ${active ? cls.on : cls.off}`}>{label}</button>;
  return (
    <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 px-5 py-4 border-t border-[#F1F5F2]" data-testid={tid}>
      <div className="min-w-0">
        <div className="font-mono text-sm font-semibold text-[#0F6B3A]">{p.codigo}</div>
        <div className="font-semibold text-[#0F2A1F] truncate">{p.nome || "—"}</div>
        <div className="font-mono text-xs text-[#5A6B62] mt-0.5 flex flex-wrap items-center gap-x-2">
          <span>Sistema: <b className="text-[#0F2A1F]">{p.fisica}</b></span><span>Alocado: <b className="text-[#0F2A1F]">{p.alocado}</b></span>
          <span>Disponível: <b className={p.disponivel <= 0 ? "text-[#B91C1C]" : "text-[#0F2A1F]"}>{p.disponivel}</b></span>
          {p.sem_estoque && <span className="px-1.5 py-0.5 bg-[#FEE2E2] text-[#B91C1C] text-[10px] font-bold uppercase" data-testid={`${tid}-sem-estoque`}>Sem estoque</span>}
          {p.verificar_loja && <span className="text-[#B8860B] text-[10px] font-bold uppercase" data-testid={`${tid}-verificar-loja`}>· verificar na loja</span>}
          {p.qtd_separar != null && <span className="text-[#2DC26B] text-[10px] font-bold uppercase">· Qtd p/ separar: {p.qtd_separar}</span>}
        </div>
        {p.conferencia && <div className="font-mono text-[11px] text-[#8A9A90] mt-0.5" data-testid={`${tid}-conf-info`}><span className={`font-bold ${st === "OK" ? "text-[#047857]" : st === "REPOR" ? "text-[#B91C1C]" : "text-[#9A6B0A]"}`}>{st}{st === "QTD" ? ` · ${p.conferencia.quantidade}` : ""}</span> · {p.conferencia.usuario} · {fmtDT(p.conferencia.at)}</div>}
      </div>
      <div className="shrink-0">
        {editing ? <QtdEditor initial={p.conferencia?.quantidade} onConfirm={(n) => act("QTD", n)} onCancel={() => setEditing(false)} testid={`${tid}-qtd`} /> : (
          <div className="flex gap-2">
            {btn("OK", "OK", { on: "bg-[#10B981] border-[#10B981] text-white", off: "bg-white border-[#A7F3D0] text-[#047857] hover:bg-[#ECFDF5]" }, active("OK"))}
            {btn("REPOR", "REPOR", { on: "bg-[#EF4444] border-[#EF4444] text-white", off: "bg-white border-[#FECACA] text-[#B91C1C] hover:bg-[#FEF2F2]" }, active("REPOR"))}
            {btn("QTD", st === "QTD" ? `QTD · ${p.conferencia.quantidade}` : "QTD", { on: "bg-[#F2B01E] border-[#F2B01E] text-white", off: "bg-white border-[#FCE6A3] text-[#9A6B0A] hover:bg-[#FFFBEE]" }, active("QTD"))}
          </div>
        )}
      </div>
    </div>
  );
}

function PulmaoPanel({ info, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 900); return () => clearTimeout(t); }, [info, onClose]);
  if (!info) return null;
  const { codigo, nome, pulmao } = info;
  return (
    <div className="fixed bottom-3 right-3 z-50 w-[220px] md:w-[300px] bg-[#0B1F17] text-white rounded-lg shadow-2xl border border-[#2DC26B] p-2.5 md:p-3 animate-in slide-in-from-bottom-4" data-testid="pulmao-panel">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-1.5 text-[#2DC26B] text-[9px] md:text-[10px] font-bold tracking-widest uppercase"><Volume2 className="w-3 h-3" /> Pulmão</div>
        <button onClick={onClose} data-testid="pulmao-panel-close" className="text-slate-300 hover:text-white"><X className="w-4 h-4" /></button>
      </div>
      <div className="font-mono text-[11px] text-slate-300 truncate">{codigo} · {nome}</div>
      {pulmao.mensagem ? <div className="mt-1.5 text-[#FCA5A5] font-semibold text-xs" data-testid="pulmao-panel-msg">{pulmao.mensagem}</div> : null}
      {pulmao.localizacoes.length > 0 && (
        <div className="mt-1.5 space-y-0.5">
          {pulmao.localizacoes.slice(0, 3).map((l) => (
            <div key={l.endereco} className="flex justify-between font-mono text-sm">
              <span className={`font-bold ${l.saldo <= 0 ? "line-through text-[#FCA5A5]" : "text-white"}`}>{l.endereco}</span>
              <span className="text-slate-300 text-xs">saldo {l.saldo}</span>
            </div>
          ))}
        </div>
      )}
      <div className="mt-2 h-0.5 bg-[#1A3A2C] rounded overflow-hidden"><div className="h-full bg-[#2DC26B] animate-[shrink_0.9s_linear_forwards]" style={{ width: "100%" }} /></div>
      <style>{`@keyframes shrink{from{width:100%}to{width:0}}`}</style>
    </div>
  );
}

export default function Picklist() {
  const [lista, setLista] = useState("base");
  const [data, setData] = useState(null);
  const [resumo, setResumo] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("todos");
  const [rack, setRack] = useState("");
  const [loading, setLoading] = useState(true);
  const [panel, setPanel] = useState(null);
  const [page, setPage] = useState(1);
  const [autoNext, setAutoNext] = useState(() => localStorage.getItem("pk_auto_next") !== "0");
  const toggleAutoNext = () => { const v = !autoNext; setAutoNext(v); localStorage.setItem("pk_auto_next", v ? "1" : "0"); };
  const pendingScroll = useRef(null);
  const dataRequest = useRef(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const refs = { 1: useRef(null), 2: useRef(null) };

  const fetchResumo = useCallback(async () => { try { setResumo((await api.get("/picklist/resumo")).data); } catch { /* ignore */ } }, []);
  const fetchData = useCallback(async (listaAlvo = lista) => {
    const requestId = ++dataRequest.current;
    setLoading(true);
    setPage(1);
    try {
      const response = await api.get("/picklist", {
        params: {
          lista: listaAlvo,
          q: q || undefined,
          status,
          rack: rack || undefined,
          page: 1,
        },
      });
      if (requestId === dataRequest.current) {
        setData(response.data);
      }
    } catch {
      if (requestId === dataRequest.current) {
        toast.error("Erro ao carregar a linha de separação");
      }
    } finally {
      if (requestId === dataRequest.current) {
        setLoading(false);
      }
    }
  }, [lista, q, status, rack]);

  const loadMore = useCallback(async () => {
    if (!data?.has_more || loadingMore) return;
    setLoadingMore(true);
    try {
      const next = page + 1;
      const { data: d } = await api.get("/picklist", { params: { lista, q: q || undefined, status, rack: rack || undefined, page: next } });
      setData((prev) => ({ ...d, grupos: [...prev.grupos, ...d.grupos] }));
      setPage(next);
    } catch { toast.error("Erro ao carregar mais endereços"); } finally { setLoadingMore(false); }
  }, [data, loadingMore, page, lista, q, status, rack]);

  useEffect(() => {
    const target = pendingScroll.current;
    if (!target) return;
    pendingScroll.current = null;
    if (target === "__more__") { loadMore(); return; }
    const el = document.querySelector(`[data-testid="${target}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("pk-next-highlight");
    setTimeout(() => el.classList.remove("pk-next-highlight"), 1600);
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const sentinel = useRef(null);
  useEffect(() => {
    if (!sentinel.current) return;
    const obs = new IntersectionObserver((entries) => { if (entries[0].isIntersecting) loadMore(); }, { rootMargin: "600px" });
    obs.observe(sentinel.current);
    return () => obs.disconnect();
  }, [loadMore]);

  useEffect(() => { fetchResumo(); }, [fetchResumo]);
  useEffect(() => { const t = setTimeout(fetchData, 250); return () => clearTimeout(t); }, [fetchData]);

  const abrirListaImportada = (listaImportada, resultado) => {
    toast.success(`Lista ${listaImportada} importada: ${resultado.itens} itens · ${resultado.unidades} un.`);
    setData(null);
    setLista(listaImportada);
    fetchResumo();
    window.setTimeout(() => fetchData(listaImportada), 150);
  };
  const job1 = useImportJob((r) => {
    abrirListaImportada("1", r);
  });
  const job2 = useImportJob((r) => {
    abrirListaImportada("2", r);
  });
  const jobs = { 1: job1, 2: job2 };

  const exportarLista = async () => {
    if (lista === "base") return;
    try {
      const res = await api.get(`/picklist/export/${lista}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `picklist-lista-${lista}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success(`Lista ${lista} exportada`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao exportar lista");
    }
  };

  const conferir = async (payload) => {
    beep("scan");
    try {
      const { data: r } = await api.post("/picklist/conferir", payload);
      if (payload.status === "OK") { beep("scan-ok"); toast.success(r.removida ? "OK · reposição removida (correção)" : "Conferido OK"); }
      else {
        beep("alert");
        setPanel({ codigo: payload.codigo, nome: r.reposicao?.descricao, pulmao: r.pulmao });
        const listaDestino = r.reposicao?.lista || payload.lista;
        const mensagem = payload.status === "REPOR"
          ? `Enviado para REPOR ${listaDestino} (1 SKU)`
          : `Enviado para REPOR ${listaDestino} · ${payload.quantidade} un.`;
        toast.success(mensagem);
      }
      const ekey = payload.endereco.replace(/\s+/g, "").toLowerCase();
      setData((prev) => {
        if (!prev) return prev;
        let novo = 0;
        const grupos = prev.grupos.map((g) => g.endereco.replace(/\s+/g, "").toLowerCase() !== ekey ? g : {
          ...g, produtos: g.produtos.map((pr) => {
            if (pr.codigo !== payload.codigo) return pr;
            if (!pr.conferencia) novo = 1;
            return { ...pr, conferencia: r.conferencia, reposicao: r.reposicao };
          }),
        });
        const conferidos = prev.progresso.conferidos + novo;
        const total = prev.progresso.total;
        if (autoNext && lista !== "base") {
          const flat = grupos.flatMap((g) => g.produtos.map((pr) => ({ g, pr })));
          const idx = flat.findIndex(({ g, pr }) => pr.codigo === payload.codigo && g.endereco.replace(/\s+/g, "").toLowerCase() === ekey);
          const next = flat.slice(idx + 1).find(({ pr }) => !pr.conferencia);
          pendingScroll.current = next ? `prod-${next.pr.codigo}-${next.g.endereco.replace(/\s+/g, "")}` : (prev.has_more ? "__more__" : null);
        }
        return { ...prev, grupos, progresso: { conferidos, total, pct: total ? Math.round((conferidos / total) * 100) : 0 } };
      });
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao conferir"); }
  };

  const limpar = async () => {
    if (!window.confirm(`Limpar a Lista ${lista}? As conferências desta lista serão apagadas.`)) return;
    try { await api.delete(`/picklist/${lista}`); toast.success(`Lista ${lista} limpa`); fetchResumo(); fetchData(); } catch { toast.error("Erro ao limpar"); }
  };

  const prog = data?.progresso || { conferidos: 0, total: 0, pct: 0 };
  const tabCls = (on) => `px-4 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors ${on ? "bg-[#2DC26B] text-white" : "text-[#0F2A1F] hover:bg-[#F1F5F2]"}`;

  return (
    <div className="space-y-5" data-testid="picklist-page">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3">
        <div>
          <div className="text-[11px] font-mono tracking-[0.2em] text-[#5A6B62] uppercase">Separação</div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0F2A1F]">Linha de Separação 1100</h1>
          <p className="text-sm text-[#5A6B62] mt-1">Confira cada produto individualmente por endereço.</p>
        </div>
        <div className="text-right flex flex-col items-end gap-2" data-testid="picklist-progresso">
          {lista !== "base" && <button data-testid="picklist-auto-next-toggle" onClick={toggleAutoNext} title="Rolar automaticamente para o próximo item pendente após conferir"
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md border text-xs font-bold transition-colors ${autoNext ? "bg-[#2DC26B] border-[#2DC26B] text-white" : "bg-white border-[#D9E3DC] text-[#5A6B62]"}`}>
            <ArrowDownToLine className="w-3.5 h-3.5" /> Próximo automático {autoNext ? "ligado" : "desligado"}
          </button>}
          <div className="font-mono text-xs text-[#5A6B62]">Progresso da conferência <b className="text-[#0F2A1F]">{prog.conferidos}/{prog.total}</b> · {prog.pct}%</div>
          <div className="h-1.5 w-56 ml-auto mt-1 bg-[#D9E3DC] rounded-full overflow-hidden"><div className="h-full bg-[#2DC26B] transition-[width] duration-500" style={{ width: `${prog.pct}%` }} /></div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {["1", "2"].map((l) => (
          <span key={l}>
            <input ref={refs[l]} type="file" accept=".pdf" className="hidden" data-testid={`picklist-import-input-${l}`}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) jobs[l].start(`/picklist/import/${l}`, f); e.target.value = ""; }} />
            <button data-testid={`picklist-import-btn-${l}`} disabled={jobs[l].running} onClick={() => refs[l].current?.click()}
              className="bg-white hover:bg-[#F1F5F2] disabled:opacity-60 text-[#0F6B3A] border border-[#0F6B3A]/40 font-semibold px-4 py-2.5 rounded-md flex items-center gap-2 text-sm">
              <FileText className="w-4 h-4" /> Importar Lista {l}
            </button>
          </span>
        ))}
        <div className="inline-flex border border-[#D9E3DC] rounded-md overflow-hidden bg-white" data-testid="picklist-tabs">
          <button data-testid="picklist-tab-base" onClick={() => setLista("base")} className={tabCls(lista === "base")}>Base completa ({resumo?.base ?? 0})</button>
          <button data-testid="picklist-tab-1" onClick={() => setLista("1")} className={tabCls(lista === "1")}>Lista 1 ({resumo?.["1"]?.total_itens ?? 0})</button>
          <button data-testid="picklist-tab-2" onClick={() => setLista("2")} className={tabCls(lista === "2")}>Lista 2 ({resumo?.["2"]?.total_itens ?? 0})</button>
        </div>
        {lista !== "base" && (
          <button
            data-testid="picklist-export-btn"
            onClick={exportarLista}
            className="bg-white hover:bg-[#F1F5F2] text-[#0F6B3A] border border-[#0F6B3A]/40 font-semibold px-4 py-2.5 rounded-md flex items-center gap-2 text-sm"
          >
            <Download className="w-4 h-4" /> Exportar Lista {lista}
          </button>
        )}
        {lista !== "base" && data?.meta && (
          <button data-testid="picklist-limpar-btn" onClick={limpar} className="bg-white hover:bg-[#FEF2F2] text-[#5A6B62] hover:text-[#B91C1C] border border-[#D9E3DC] font-semibold px-4 py-2.5 rounded-md flex items-center gap-2 text-sm"><Trash2 className="w-4 h-4" /> Limpar Lista {lista}</button>
        )}
      </div>
      {(job1.job || job2.job) && <div className="bg-white border border-[#D9E3DC] rounded-lg p-3"><ImportProgress job={job1.job?.status === "processando" || !job2.job ? job1.job : job2.job} testid="picklist-import-progress" /></div>}
      {data?.meta && <div className="font-mono text-[11px] text-[#8A9A90] -mt-2" data-testid="picklist-meta">Lote {data.meta.lote} · {data.meta.data} {data.meta.hora} · {data.meta.total_itens} itens / {data.meta.total_unidades} un. · Importada por {data.meta.importado_por} · {fmtDT(data.meta.importado_em)}</div>}

      <div className="bg-white border border-[#D9E3DC] rounded-lg shadow-sm p-3 grid grid-cols-1 md:grid-cols-6 gap-3">
        <div className="relative md:col-span-4">
          <Search className="w-4 h-4 text-[#8A9A90] absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="picklist-busca-input" value={q} onChange={(e) => setQ(e.target.value)} className={`${inputCls} pl-9`} placeholder="Buscar por endereço, código ou descrição... (vários códigos: separe com ;)" />
        </div>
        <select data-testid="picklist-status-select" value={status} onChange={(e) => setStatus(e.target.value)} className={inputCls}>
          <option value="todos">Todos</option><option value="pendentes">Pendentes</option><option value="OK">OK</option><option value="REPOR">REPOR</option><option value="QTD">QTD</option><option value="sem_estoque">Sem estoque</option>
        </select>
        <select data-testid="picklist-rack-select" value={rack} onChange={(e) => setRack(e.target.value)} className={inputCls}>
          <option value="">Todos racks</option>{(data?.racks || []).map((r) => <option key={r} value={r}>Rack {r}</option>)}
        </select>
      </div>

      {loading && !data ? <div className="py-16 text-center text-[#8A9A90]">Carregando...</div>
        : !data || data.grupos.length === 0 ? (
          <div className="bg-white border border-[#D9E3DC] rounded-lg py-16 text-center" data-testid="picklist-empty">
            <div className="text-[#0F2A1F] font-semibold">{lista !== "base" && !data?.meta ? `Nenhuma Lista ${lista} importada.` : "Nenhum endereço encontrado."}</div>
            <div className="text-xs text-[#8A9A90] mt-1">
              {lista !== "base" && !data?.meta
                ? "Importe o PDF da Picking List para carregar os itens."
                : "Ajuste a busca ou os filtros."}
            </div>
          </div>
        ) : (
          <div className="space-y-4" data-testid="picklist-grupos">
            <div className="font-mono text-[11px] text-[#8A9A90]" data-testid="picklist-grupos-info">Mostrando {data.grupos.length} de {data.total_grupos} endereços</div>
            {data.grupos.map((g) => (
              <div key={g.endereco} className={`bg-white border rounded-lg shadow-sm ${g.fora_da_base ? "border-[#FCE6A3]" : "border-[#D9E3DC]"}`} data-testid={`grupo-${g.endereco.replace(/\s+/g, "")}`}>
                <div className="flex items-center gap-3 px-5 py-3 bg-[#F4F8F5] rounded-t-lg">
                  <MapPin className="w-4 h-4 text-[#0F6B3A]" />
                  <span className="font-mono text-xl font-bold text-[#0F2A1F] tracking-wide">{g.endereco}</span>
                  <span className="font-mono text-[11px] text-[#8A9A90]">{g.produtos.length} produto(s)</span>
                  {g.fora_da_base && <span className="ml-auto text-[10px] font-bold uppercase text-[#9A6B0A] bg-[#FFF4D6] px-2 py-0.5 rounded">Sem endereço na base de separação</span>}
                </div>
                {g.produtos.map((p) => <ProdutoRow key={p.id} p={p} grupo={g} lista={lista} onConferir={conferir} />)}
              </div>
            ))}
            <div ref={sentinel} className="py-4 text-center">
              {data.has_more ? (
                <button data-testid="picklist-load-more" onClick={loadMore} disabled={loadingMore} className="bg-white hover:bg-[#F1F5F2] text-[#0F6B3A] border border-[#D9E3DC] font-semibold px-5 py-2.5 rounded-md text-sm disabled:opacity-60">
                  {loadingMore ? "Carregando..." : `Carregar mais endereços (${data.total_grupos - data.grupos.length} restantes)`}
                </button>
              ) : <span className="font-mono text-[11px] text-[#8A9A90]">Fim da lista</span>}
            </div>
          </div>
        )}
      <PulmaoPanel info={panel} onClose={() => setPanel(null)} />
    </div>
  );
}
